<script setup lang="ts">
/**
 * /monitor —— 监管端综合监控(v2 · G5,1920×1080 全屏,S-50)。
 *
 * ═══ 这一页没有壳(App.vue 按路由跳过,与 /dashboard 同一种做法)═══
 *
 * 画板把它画成整块 1920×1080 的管理台:顶栏(品牌 + 页签 + 天气/日期/时钟 + 只读徽标)、
 * 220 宽的左侧导航、中央列(KPI 行 / 实时轨迹 / 底行)、470 宽的右栏。没有内容区留白 ——
 * 所以这一页自己就是整屏,自己挂深色令牌变量(useToneVars,与 AppShell 同一处收口)。
 * **离开这一页靠顶栏页签与侧栏导航**(画板把两个导航件都画在这一页上,与 G1 不同 ——
 * G1 的画板没有导航件,只能靠浏览器返回;这里的页签与侧栏就是真实入口)。
 *
 * ═══ 页签与侧栏按真实页面清单重建(Y-32)═══
 *
 * 画板顶栏画了七个页签、侧栏画了八个导航项,其中大半是死入口(航线航迹 / 船舶管理 /
 * 起降场管理 / 视频中心 / 告警事件 / 统计分析:平台里没有这些页面,船舶域与起降场
 * 整块不存在)。按 App.vue 的 NAV 那份真实页面清单,四项就是全部:数据大屏 / 订单 /
 * 起飞申请(S-53 加的第四项,排在订单之后) / 综合监控 ——
 * 页签与侧栏各列一次,当前页「综合监控」在两边都是激活态。画板那六个死项逐条登记在
 * design-copy.json 的 _notRequired 里(不渲染死入口;「导航里出现一个点不开的东西,
 * 演示时就是一句解释不清的话」)。画板侧栏第二项叫「订单监管」,而全仓的导航项叫「订单」
 * (App.vue 的 NAV)—— 同一入口两个名字必有一处错,与「订单」对齐(登记为豁免)。
 *
 * ═══ 两组数据:虚拟的船舶世界与真实的订单世界(Y-34,2026-10-16 人裁决)═══
 *
 *   虚拟(确定性固定值,全部来自 ../demo/g1-demo-data.ts,逐块 @unimplemented):
 *     KPI 四张卡(与 G1 逐值共享 42 / 82 / 42 / 86)、运行状态四行(36 / 8 / 3 / 5)、
 *     船舶列表五行、地图标注(三个起降场 / 两枚船位 / 锚 / 走私预警圈)、天气、
 *     底行实时事件四条与六枚统计块(计数与子行)、「视频留存 & 回看」的四行检索值
 *     与时间轴刻度(形态占位的演示值)。
 *
 *   真实(一个都不许被虚拟值顶掉 —— 这是 Y-34 的另一半):
 *     ① 顶栏东八区时钟 = 真时刻(秒级,格式走 formatShanghaiDateTime,Y-03);
 *     ② 顶栏日期 = 真日期(东八区)+ 星期;
 *     ③ 侧栏「订单口径」小卡 = 区间上真实的单量 / 架次 / 在途(GET /api/gov/dashboard,
 *        区间 = 本月 1 号 ~ 今天,东八区 —— 口径与 G1 的页脚同款,AS-096 / AS-097)。
 *     这张小卡是画板没画的补件(S-49 的「订单卡页脚」在这块屏上没有对应卡片,
 *     真数不能因此掉出屏外);它住在侧栏中部 —— 三项导航只占 132 高,那里本来空着。
 *
 * ═══ 地图 = 本地演示位图 + DOM 标注层 ═══
 *
 * 底图 public/g5-map.png 是从仓库根拷进来的卫星演示图(本地资源,不是瓦片、不是地图引擎);
 * 其上的标注(起降场 / 船 / 锚 / 预警圈 / 图例 / 比例尺)是 DOM,坐标是**画板写死的像素位**
 * (G5_DEMO_MAP),没有经纬度也没有投影口径 —— 装饰口径,真实性等甲方给出地图口径(AS-097)。
 * 画板右栏那块「视频留存 & 回看」的回放时间轴是本板唯一带图型的元素(轨道 / 已播段 / 告警段),
 * 它是形态占位:不接任何数据(见下方 @unimplemented 与口径浮层)。
 *
 * ═══ 零金额 ═══
 *
 * 这一页没有任何金额字段(L0-01 / M-07):真实数据只有订单口径的三个计数,
 * 演示数据全是船与事件的计数/名称。
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { NConfigProvider, darkTheme, zhCN } from 'naive-ui'
import { formatShanghaiDate, formatShanghaiDateTime, toShanghaiParts } from '@sea-drone/shared'
import {
  govThemeOverrides,
  NavIcon,
  ScopeNotes,
  useToneVars,
  type NavItem,
  type ScopeNote,
} from '@sea-drone/ui'
import { api, ApiError, type GovDashboard } from '../api/client'
import {
  G1_DEMO_DEPARTMENTS,
  G1_DEMO_EVENTS,
  G1_DEMO_KPI,
  G1_DEMO_VIDEO_CELLS,
  G5_DEMO_EVENT_ROWS,
  G5_DEMO_EVENT_SUBLINES,
  G5_DEMO_MAP,
  G5_DEMO_RUN_STATUS,
  G5_DEMO_SHIPS,
  G5_DEMO_TIMELINE_TICKS,
  G5_DEMO_VIDEO_SEARCH,
  G5_DEMO_WEATHER,
} from '../demo/g1-demo-data'

/** 深色令牌变量:这一页不在 AppShell 里,自己挂(与 AppShell / DashboardView 同一处收口) */
const vars = useToneVars(() => 'dark')

// ── 顶栏页签与侧栏导航:同一份四项(App.vue 的 NAV)────────────
//
// 标签与 App.vue 的 NAV 同名同序(那里是全仓的侧栏导航清单);这里写一份字面量而不是
// import App.vue —— admin-nav 守卫要求 NAV 以字面量住在 App.vue 里(它按那份清单
// 做 NAV ↔ 路由 ↔ 视图文件的三向核对),而本页这一栏是画在页面里的**第二处**手写导航。
// **这份手写清单与 App.vue 的 NAV 逐项对齐(标签 / to / 顺序)由 admin-nav 守卫的
// 「页面内导航清单」判据盯着**:加第四项「起飞申请」时若只改壳那份、漏了这份,页面上
// 看不出异常,只是从监控页进不去新页 —— 那正是这条判据要抓的漂移。

const route = useRoute()

const SIDE_NAV: readonly NavItem[] = [
  { key: 'dashboard', label: '数据大屏', to: '/dashboard', icon: 'dashboard' },
  { key: 'orders', label: '订单', to: '/orders', icon: 'list' },
  { key: 'takeoff-applications', label: '起飞申请', to: '/takeoff-applications', icon: 'clipboard' },
  { key: 'monitor', label: '综合监控', to: '/monitor', icon: 'monitor' },
]

/** 当前页高亮。与 AppShell 同口径的精确匹配(本页没有带参数的子路由) */
function isCurrent(to: string): boolean {
  return route.path === to
}

// ── 顶栏:东八区时钟与日期(真数,Y-03)──────────────────────

const now = ref(new Date())
let clockTimer: ReturnType<typeof setInterval> | null = null
const clockText = computed(() => formatShanghaiDateTime(now.value).slice(11))

/**
 * 顶栏日期 = 真日期(东八区)+ 星期。星期**不走本机时区的 getDay,也不走 Intl**
 * (ICU 依赖,与 Y-03 的理由同一条):按东八区重建那一天再取 getUTCDay。
 * 查表越界给「星期?」不是兜底 —— getUTCDay 的值域恒为 0..6,这一档不可达;
 * 写它是让「表短了一格」这种改动在页面上现形,而不是悄悄少两个字。
 */
const WEEKDAY_NAMES = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'] as const

function dateLine(at: Date): string {
  const p = toShanghaiParts(at)
  const weekday = WEEKDAY_NAMES[new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay()] ?? '星期?'
  return `${formatShanghaiDate(at)} ${weekday}`
}

const dateText = computed(() => dateLine(now.value))

/** 顶栏天气:虚拟演示值(没有来源,见 G5_DEMO_WEATHER 的 @unimplemented) */
const weatherText = G5_DEMO_WEATHER.text

// ── 订单口径小卡(真数,与 G1 页脚同一口径)───────────────────

const dashboard = ref<GovDashboard | null>(null)
const statsError = ref<string | null>(null)
const rangeFrom = ref('')
const rangeTo = ref('')
const rangeText = computed(() => `${rangeFrom.value} 至 ${rangeTo.value}`)

/** 统一那句错话:`[CODE] 消息（requestId xxx）`(client.ts 拼的),不许吞成一句「加载失败」 */
function errorText(e: unknown): string {
  if (e instanceof ApiError) return `[${e.code}] ${e.message}（requestId ${e.requestId}）`
  return e instanceof Error ? e.message : String(e)
}

/**
 * 区间 = 本月 1 号 ~ 今天(东八区)—— 与 G1 的页脚同款、同一个真查询参数(AS-096 的口径,
 * G5 沿用并记进 AS-097):挂载时定下来、页面存续期间不再变,文字与数出同一处。
 */
async function loadStats(): Promise<void> {
  try {
    dashboard.value = await api.dashboard({ from: rangeFrom.value, to: rangeTo.value })
    statsError.value = null
  } catch (e) {
    dashboard.value = null
    statsError.value = errorText(e)
  }
}

// ── 虚拟演示数据:文案住这一页,数据全部来自演示模块(Y-34)────

/**
 * 船舶/无人机的五个状态词与色组(画板逐字:运行中 / 准备起降 / 预警告警 / 不在飞 / 锚泊中)。
 * **一个状态只在这里定义一次** —— 图例、运行状态卡、船舶列表三处都引用这张表。
 * 色组走语义位(success / info / danger / 灰),与 status.ts 给订单状态分组的做法同构;
 * G1 的图例词汇是另一套(运输中 / 准备运输 / 预警异常)—— 两板各照各的画板锁定,
 * 同语义位的色差(准备运输=黄 / 准备起降=蓝)保持原样,是否统一由人对齐(见切片备注)。
 */
const SHIP_STATUS: Readonly<
  Record<'running' | 'landing' | 'alert' | 'anchored' | 'idle', { readonly label: string; readonly tone: 'success' | 'info' | 'danger' | 'muted' }>
> = {
  running: { label: '运行中', tone: 'success' },
  landing: { label: '准备起降', tone: 'info' },
  alert: { label: '预警告警', tone: 'danger' },
  anchored: { label: '锚泊中', tone: 'muted' },
  idle: { label: '不在飞', tone: 'muted' },
}

/**
 * KPI 四张卡(画板逐字;数字与 G1 逐值共享 —— G1_DEMO_KPI)。
 * 键、图标与 G1 那四张一一对应,第 5 张「运行状态」单独画(不是数值卡)。
 */
const kpiCards = [
  { key: 'inVoyage', icon: 'ship', label: '在航船舶 · 艘', value: G1_DEMO_KPI.inVoyage, caption: '此刻在舟山海域航行的船舶' },
  { key: 'innerAnchorage', icon: 'anchor', label: '内锚地锚泊 · 艘', value: G1_DEMO_KPI.innerAnchorage, caption: '内锚地待泊与作业中的船舶' },
  { key: 'outerAnchorage', icon: 'anchor', label: '外锚地锚泊 · 艘', value: G1_DEMO_KPI.outerAnchorage, caption: '外锚地锚泊、需无人机送件的船舶' },
  { key: 'agencyToday', icon: 'clipboard', label: '今日外轮代理单 · 单', value: G1_DEMO_KPI.agencyToday, caption: '船员证书与物料送上锚轮、边检盖章' },
] as const

/** 运行状态卡四行(画板次序:运行中 / 准备起降 / 预警告警 / 不在飞;词走 SHIP_STATUS) */
const runRows = [
  { key: 'running', ...SHIP_STATUS.running, count: G5_DEMO_RUN_STATUS.running },
  { key: 'landing', ...SHIP_STATUS.landing, count: G5_DEMO_RUN_STATUS.landing },
  { key: 'alert', ...SHIP_STATUS.alert, count: G5_DEMO_RUN_STATUS.alert },
  { key: 'idle', ...SHIP_STATUS.idle, count: G5_DEMO_RUN_STATUS.idle },
] as const

/** 地图图例四项(画板次序;词与点色都走 SHIP_STATUS) */
const mapLegend = [SHIP_STATUS.running, SHIP_STATUS.landing, SHIP_STATUS.alert, SHIP_STATUS.idle] as const

/** 船舶搜索框:筛的就是屏幕上那份固定的五行(演示数据内的过滤,不接任何数据源) */
const shipQuery = ref('')
const visibleShips = computed(() => {
  const q = shipQuery.value.trim()
  if (q === '') return G5_DEMO_SHIPS
  return G5_DEMO_SHIPS.filter((s) => s.name.includes(q))
})

/**
 * 部门名(画板逐字;与 G1 的同一张表 —— 它不是枚举,是演示词汇,按 G1 先例住在页面里)。
 * 查不到的键显示机器键本身(与 statusLabel 同一做法:让它刺眼,而不是编一个名字)。
 */
const DEPARTMENT_NAMES: Record<string, string> = {
  msa: '海事',
  customs: '海关',
  immigration: '边检',
  port: '港航',
  police: '公安',
  emergency: '应急',
}
const departments = G1_DEMO_DEPARTMENTS.map((d) => ({ ...d, name: DEPARTMENT_NAMES[d.key] ?? d.key }))

/** 六路直播格:与 G1 同一份演示数据与折叠规则(小写机器取值 → 画板上那两个词) */
const videoCells = G1_DEMO_VIDEO_CELLS.map((cell) => ({
  ...cell,
  badgeText: cell.badge === 'live' ? 'LIVE' : 'REC',
  badgeClass: cell.badge === 'live' ? 'video-badge--live' : 'video-badge--rec',
}))

/** 底行「实时事件」四条(时刻/文本/部门名/点色;部门名走 DEPARTMENT_NAMES) */
const eventRows = G5_DEMO_EVENT_ROWS.map((row) => ({
  ...row,
  deptName: DEPARTMENT_NAMES[row.dept] ?? row.dept,
}))

/** 走私预警那句词(地图标签与统计块 3 同名)—— 同一帧同词只锁一条,这里也只写一处 */
const SMUGGLING_LABEL = '走私监管预警'

/** 底行六枚统计块(名字与 G1 的六张事件卡同名同色;计数来自 G1_DEMO_EVENTS,子行来自 G5) */
const statBlocks = [
  { key: 'ship', label: '船舶事件', tone: 'info', count: G1_DEMO_EVENTS.ship, sub: G5_DEMO_EVENT_SUBLINES.ship },
  { key: 'videoMonitor', label: '视频监管事件', tone: 'info', count: G1_DEMO_EVENTS.videoMonitor, sub: G5_DEMO_EVENT_SUBLINES.videoMonitor },
  { key: 'smuggling', label: SMUGGLING_LABEL, tone: 'danger', count: G1_DEMO_EVENTS.smuggling, sub: G5_DEMO_EVENT_SUBLINES.smuggling },
  { key: 'customs', label: '海关验放存档', tone: 'success', count: G1_DEMO_EVENTS.customs, sub: G5_DEMO_EVENT_SUBLINES.customs },
  { key: 'immigration', label: '边检登轮记录', tone: 'success', count: G1_DEMO_EVENTS.immigration, sub: G5_DEMO_EVENT_SUBLINES.immigration },
  { key: 'aisAlarm', label: 'AIS 信号告警', tone: 'warning', count: G1_DEMO_EVENTS.aisAlarm, sub: G5_DEMO_EVENT_SUBLINES.aisAlarm },
] as const

/** 地图标注(G5_DEMO_MAP):三个起降场 / 两枚船位 / 锚位 / 预警圈 / 比例尺 —— 全在演示模块 */
const mapPads = G5_DEMO_MAP.pads
const mapShips = G5_DEMO_MAP.ships
const mapAnchor = G5_DEMO_MAP.anchor
const mapWarning = G5_DEMO_MAP.warning
const mapScaleText = G5_DEMO_MAP.scaleText

/** 「视频留存 & 回看」四行检索的标签(画板逐字;值来自演示模块,整块是形态占位) */
const videoSearchRows = [
  { key: 'agency', label: '监管单位', value: G5_DEMO_VIDEO_SEARCH.agency },
  { key: 'camera', label: '摄像头点位', value: G5_DEMO_VIDEO_SEARCH.camera },
  { key: 'range', label: '时间区间', value: G5_DEMO_VIDEO_SEARCH.range },
  { key: 'ship', label: '关联船名', value: G5_DEMO_VIDEO_SEARCH.ship },
] as const

const timelineTicks = G5_DEMO_TIMELINE_TICKS

/**
 * 口径说明浮层(演示时点开就能答):视频占位(Y-33)与虚拟/真实边界(Y-34)。
 * 它是画板没画的补件 —— 与 S-49 在订单卡标题行加的那枚同一个理由。
 */
const CARD_NOTES: readonly ScopeNote[] = [
  {
    title: '视频与监管仓是演示占位(Y-33 / 甲方 2026-10-14 口径)',
    body:
      '六路视频格、六个部门接入位与「视频留存 & 回看」整块都是演示占位:不接真实视频流、不接真实系统 —— ' +
      '视频直播与回放整块不在本期(L0-06 搁置、M-09 排除、BLK-02 未解除)。四行检索、回放时间轴与' +
      '「检索回放」「证据归档」两个按钮在本期都不接任何数据;那两个按钮在页面上是禁用态,不是可点的死入口。',
    refs: ['Y-33', 'L0-06', 'M-09'],
  },
  {
    title: '这块屏上哪些是演示数据、哪些是真数(Y-34)',
    body:
      'KPI 四张卡、运行状态、船舶列表、地图标点与预警圈、天气、底行六枚统计块都是演示虚拟数据' +
      '(确定性固定值,不接真实数据源 —— 船舶世界在本平台没有数据模型,甲方口径待给出)。' +
      '真实数据有三处:顶栏的东八区时钟、顶栏日期、侧栏「订单口径」小卡那三个数(区间 = 本月 1 号 ~ 今天)—— ' +
      '它们一个都不会被演示数据顶掉。',
    refs: ['Y-34', 'Y-15'],
  },
]

onMounted(() => {
  rangeTo.value = formatShanghaiDate(new Date())
  rangeFrom.value = `${rangeTo.value.slice(0, 8)}01`
  clockTimer = setInterval(() => {
    now.value = new Date()
  }, 1000)
  void loadStats()
})

// 不清理就是每秒一次的内存泄漏 —— 路由切走之后这个定时器还在跑
onUnmounted(() => {
  if (clockTimer !== null) {
    clearInterval(clockTimer)
    clockTimer = null
  }
})
</script>

<template>
  <n-config-provider :theme="darkTheme" :theme-overrides="govThemeOverrides" :locale="zhCN">
    <div class="screen" :style="vars">
      <div class="stage">
        <!-- ── 顶栏 ─────────────────────────────────────── -->
        <header class="topbar">
          <div class="tb-brand">
            <span class="brand-mark" aria-hidden="true"><NavIcon name="plane" :size="16" /></span>
            <div class="tb-brand-text">
              <span class="brand-name">无人机海运物流</span>
              <span class="brand-sub">监管端 · 综合监控</span>
            </div>
          </div>

          <!-- 页签 = Y-32 的真实三项(死页签不渲染,登记在 design-copy 的 _notRequired) -->
          <nav class="tabs" aria-label="页面">
            <router-link
              v-for="t in SIDE_NAV"
              :key="t.key"
              class="tab"
              :class="{ 'tab--active': isCurrent(t.to) }"
              :to="t.to"
            >
              {{ t.label }}
            </router-link>
          </nav>

          <div class="tb-right">
            <div class="weather">
              <span class="weather-text">{{ weatherText }}</span>
              <span class="weather-date">{{ dateText }}</span>
            </div>
            <div class="clock-block">
              <span class="clock-time">{{ clockText }}</span>
              <span class="clock-zone">东八区</span>
            </div>
            <span class="readonly-badge">全程只读</span>
          </div>
        </header>

        <!-- ── 主区:左侧导航 / 中央列 / 右栏 ─────────────── -->
        <main class="main">
          <aside class="sidebar">
            <nav class="side-nav">
              <router-link
                v-for="item in SIDE_NAV"
                :key="item.key"
                class="nav-item"
                :class="{ 'nav-item--active': isCurrent(item.to) }"
                :to="item.to"
              >
                <NavIcon :name="item.icon" :size="18" />
                <span class="nav-label">{{ item.label }}</span>
              </router-link>
            </nav>

            <!-- 订单口径小卡 = 真数落点(画板没画,见文件头;S-49 的页脚在这块屏上的等价物) -->
            <article class="order-scope">
              <h4 class="os-title">订单口径</h4>
              <p class="os-range">{{ rangeText }}</p>
              <template v-if="statsError !== null">
                <p class="os-error">{{ statsError }}</p>
              </template>
              <template v-else-if="dashboard !== null">
                <div class="os-row"><span class="os-label">单量</span><span class="os-value">{{ dashboard.orderCount }} 单</span></div>
                <div class="os-row"><span class="os-label">架次</span><span class="os-value">{{ dashboard.flightCount }}</span></div>
                <div class="os-row"><span class="os-label">此刻在途</span><span class="os-value">{{ dashboard.inTransitCount }} 单</span></div>
              </template>
              <p v-else class="os-loading">取数中…</p>
            </article>

            <div class="sidebar-foot">
              <figure class="hero">
                <img class="hero-img" src="/home-hero.png" alt="" />
              </figure>
              <p class="sidebar-note">面向海关、边检、海事、港航、公安、应急的只读监管入口。</p>
            </div>
          </aside>

          <section class="center">
            <!-- KPI 行:四张数值卡(与 G1 逐值共享)+「运行状态」卡 -->
            <section class="kpi-row">
              <article v-for="card in kpiCards" :key="card.key" class="kpi-card">
                <div class="kpi-head">
                  <NavIcon class="kpi-icon" :name="card.icon" :size="20" />
                  <span class="kpi-label">{{ card.label }}</span>
                </div>
                <div class="kpi-value">{{ card.value }}</div>
                <p class="kpi-caption">{{ card.caption }}</p>
              </article>

              <article class="run-card">
                <h3 class="run-title">运行状态</h3>
                <ul class="run-rows">
                  <li v-for="row in runRows" :key="row.key" class="run-row">
                    <i class="run-dot" :class="`run-dot--${row.tone}`" aria-hidden="true" />
                    <span class="run-name">{{ row.label }}</span>
                    <span class="run-count">{{ row.count }}</span>
                  </li>
                </ul>
              </article>
            </section>

            <!-- 实时轨迹:船舶列表 + 地图(底图 g5-map.png + DOM 标注层) -->
            <section class="track-panel">
              <header class="track-head">
                <div class="track-title">
                  <h2>实时轨迹</h2>
                  <span class="track-sub">舟山海域 · 内外锚地 · 多部门并联</span>
                </div>
                <label class="ship-search">
                  <NavIcon class="ship-search-icon" name="search" :size="14" />
                  <input
                    v-model="shipQuery"
                    class="ship-search-input"
                    type="search"
                    placeholder="搜索船名、航次号、起降场…"
                  />
                </label>
              </header>

              <div class="track-body">
                <ul class="ship-list">
                  <li v-for="ship in visibleShips" :key="ship.name" class="ship-row">
                    <NavIcon class="ship-icon" name="ship" :size="18" />
                    <div class="ship-text">
                      <span class="ship-name">{{ ship.name }}</span>
                      <span class="ship-meta">航向 {{ ship.heading }} · {{ ship.speed }}</span>
                    </div>
                    <span class="ship-status" :class="`ship-status--${SHIP_STATUS[ship.status].tone}`">
                      {{ SHIP_STATUS[ship.status].label }}
                    </span>
                  </li>
                  <!-- 搜索颗粒无收时要说出来,不许留一块空白 -->
                  <li v-if="visibleShips.length === 0" class="ship-empty">没有匹配的船舶</li>
                </ul>

                <div class="map">
                  <img
                    class="map-img"
                    src="/g5-map.png"
                    alt="舟山海域演示底图:三处起降场、两枚船位与走私监管预警圈(演示数据,非实况)"
                  />
                  <!-- 标注层:坐标是画板写死的像素位(G5_DEMO_MAP),装饰口径 -->
                  <div class="map-layer">
                    <span
                      v-for="pad in mapPads"
                      :key="pad.name"
                      class="pad"
                      :style="{ left: `${pad.x}px`, top: `${pad.y}px` }"
                    >
                      <i class="pad-dot" aria-hidden="true" />{{ pad.name }}
                    </span>

                    <span
                      v-for="(pos, i) in mapShips"
                      :key="`ship-${i}`"
                      class="map-ship"
                      :style="{ left: `${pos.x}px`, top: `${pos.y}px` }"
                    >
                      <NavIcon name="ship" :size="22" />
                    </span>

                    <span
                      class="map-anchor"
                      :style="{ left: `${mapAnchor.x}px`, top: `${mapAnchor.y}px` }"
                    >
                      <NavIcon name="anchor" :size="16" />
                    </span>

                    <div
                      class="warning-circle"
                      :style="{ left: `${mapWarning.x}px`, top: `${mapWarning.y}px` }"
                    >
                      <span class="warning-tag">{{ SMUGGLING_LABEL }}</span>
                    </div>

                    <ul class="map-legend">
                      <li v-for="item in mapLegend" :key="item.label" class="legend-item">
                        <i class="legend-dot" :class="`legend-dot--${item.tone}`" aria-hidden="true" />
                        <span>{{ item.label }}</span>
                      </li>
                    </ul>
                    <span class="map-scale">{{ mapScaleText }}</span>
                  </div>
                </div>
              </div>
            </section>

            <!-- 底行:实时事件 + 六枚统计块 -->
            <section class="bottom-row">
              <article class="events-card">
                <header class="events-head">
                  <span class="events-title">实时事件</span>
                  <span class="events-more">全部部门 ›</span>
                </header>
                <ul class="events-list">
                  <li v-for="(ev, i) in eventRows" :key="`${ev.time}-${i}`" class="event-row">
                    <i class="event-dot" :class="`event-dot--${ev.tone}`" aria-hidden="true" />
                    <span class="event-time">{{ ev.time }}</span>
                    <span class="event-text">{{ ev.text }}</span>
                    <span class="event-dept" :class="`event-dept--${ev.tone}`">{{ ev.deptName }}</span>
                  </li>
                </ul>
              </article>

              <article v-for="b in statBlocks" :key="b.key" class="stat-block">
                <span class="stat-name">{{ b.label }}</span>
                <span class="stat-count" :class="`stat-count--${b.tone}`">{{ b.count }} 条</span>
                <span class="stat-sub">{{ b.sub }}</span>
              </article>
            </section>
          </section>

          <aside class="rail">
            <!-- 多部门监管仓(Y-33 演示占位;口径小字是画板逐字,给甲方看的) -->
            <article class="warehouse-card">
              <span class="brackets" aria-hidden="true" />
              <h3 class="card-title">多部门监管仓</h3>
              <p class="warehouse-note">海事、海关、边检、港航、公安、应急统一接入;以下画面为演示占位,实现时接虚拟数据源。</p>
              <div class="dept-row">
                <span v-for="d in departments" :key="d.key" class="dept-chip">
                  <i class="dept-dot" :class="`dept-dot--${d.tone}`" aria-hidden="true" />{{ d.name }}
                </span>
              </div>
              <div class="video-grid">
                <figure v-for="cell in videoCells" :key="cell.ch" class="video-cell">
                  <img class="video-img" :src="`/${cell.img}`" :alt="`${cell.point} 演示画面(非实况)`" />
                  <span class="video-scrim" aria-hidden="true" />
                  <figcaption class="video-top">
                    <span class="video-badge" :class="cell.badgeClass">
                      <i class="video-badge-dot" aria-hidden="true" />{{ cell.badgeText }}
                    </span>
                    <span class="video-ch">{{ cell.ch }}</span>
                  </figcaption>
                  <span class="video-point">{{ cell.point }}</span>
                </figure>
              </div>
            </article>

            <!--
              视频留存 & 回看:整块形态占位 —— 四行检索 / 回放时间轴 / 两个按钮
              全都不接任何数据,两个按钮是禁用态(不是可点的死入口)。

              @unimplemented(reason="按甲方 2026-10-14 口径,演示占位,不接真实视频流/真实系统 —— 视频直播、三天回放、断流告警整块不在本期(L0-06 搁置、M-09 排除、BLK-02 未解除);四行检索的选项集(监管单位 / 摄像头总路数 / 关联船名)没有任何模型支撑,「证据归档」这个动作在决策表里也不存在(M-04 的证据链封存是服务端行为,不是一个归档按钮)", owner="me", blocks="G5 右栏「视频留存 & 回看」整块")
            -->
            <article class="retention-card">
              <span class="brackets" aria-hidden="true" />
              <header class="vrc-head">
                <h3 class="card-title">视频留存 & 回看</h3>
                <ScopeNotes tone="dark" :notes="CARD_NOTES" />
              </header>

              <dl class="search-rows">
                <div v-for="row in videoSearchRows" :key="row.key" class="search-row">
                  <dt>{{ row.label }}</dt>
                  <dd>{{ row.value }}</dd>
                </div>
              </dl>

              <div class="timeline" aria-hidden="true">
                <div class="tl-track">
                  <span class="tl-rail" />
                  <span class="tl-played" />
                  <span class="tl-alarm" />
                </div>
                <div class="tl-ticks">
                  <span v-for="t in timelineTicks" :key="t">{{ t }}</span>
                </div>
              </div>

              <!-- 禁用态而不是活按钮:一个点得动的按钮会让人以为这件事做得成 -->
              <div class="act-row">
                <button class="act act--primary" type="button" disabled title="演示占位:本期不接数据">
                  检索回放
                </button>
                <button class="act" type="button" disabled title="演示占位:本期不接数据">证据归档</button>
              </div>
            </article>
          </aside>
        </main>
      </div>
    </div>
  </n-config-provider>
</template>

<style scoped>
/* ═══ 整屏与舞台 ═══════════════════════════════════════
   固定定位铺满视口;舞台锁 1920×1080(投屏目标),视口更小时整体滚动 ——
   与 /dashboard(S-49)同一套机制。 */
.screen {
  position: fixed;
  inset: 0;
  overflow: auto;
  background: var(--sd-bg-deep);
  color: var(--sd-text1);
  font-family: var(--sd-font-sans);
}

.stage {
  position: relative;
  width: 1920px;
  height: 1080px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  font-variant-numeric: tabular-nums;
}

/* 画板上的宽高都是**含内边距**的:border-box 不打,「470 宽的卡」会撑成 510,
   底下那张卡直接被挤出屏幕 —— S-49 实测过一次。 */
.stage,
.stage * {
  box-sizing: border-box;
}

/* ═══ 顶栏(高 88,画板 TopBar)══════════════════════ */
.topbar {
  flex: none;
  height: 88px;
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 0 24px;
  background: var(--sd-bg);
}

.tb-brand {
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-mark {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--sd-accent);
  color: var(--sd-bg-deep);
  display: flex;
  align-items: center;
  justify-content: center;
}

.tb-brand-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.brand-name {
  font-size: 16px;
  color: var(--sd-text1);
  white-space: nowrap;
}

.brand-sub {
  font-size: 11px;
  color: var(--sd-text3);
  white-space: nowrap;
}

.tabs {
  display: flex;
  align-items: center;
  gap: 80px;
  margin: 0 auto;
}

/* 激活页签 = 强调色字 + 56×2 下划线(画板) */
.tab {
  position: relative;
  font-size: 14px;
  color: var(--sd-text2);
  text-decoration: none;
  white-space: nowrap;
  padding: 6px 0;
}

.tab--active {
  color: var(--sd-accent);
}

.tab--active::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -4px;
  width: 56px;
  height: 2px;
  background: var(--sd-accent);
}

.tb-right {
  flex: none;
  display: flex;
  align-items: center;
  gap: 20px;
}

.weather {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.weather-text {
  font-size: 13px;
  color: var(--sd-text2);
  white-space: nowrap;
}

.weather-date {
  font-size: 11px;
  color: var(--sd-text3);
  white-space: nowrap;
}

.clock-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.clock-time {
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02em;
  color: var(--sd-accent);
}

.clock-zone {
  font-size: 11px;
  color: var(--sd-text3);
}

/* 只读徽标(画板:$gov-tag-draft 一族 —— 底 + 描边 + 灰蓝字;与 G1 同款) */
.readonly-badge {
  flex: none;
  padding: 8px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border-strong);
  background: var(--sd-surface-alt);
  font-size: 13px;
  color: var(--sd-text2);
  white-space: nowrap;
}

/* ═══ 主区(顶栏与底边之间,内衬 14,三列间距 14)═══════ */
.main {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 14px;
  padding: 14px;
}

/* ── 左侧导航(220,画板 Sidebar)───────────────────── */

.sidebar {
  flex: none;
  width: 220px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 4px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
}

.side-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-item {
  height: 44px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  border-radius: var(--sd-radius-sm);
  font-size: 14px;
  color: var(--sd-text2);
  text-decoration: none;
}

.nav-item :deep(.nav-icon) {
  color: var(--sd-text3);
}

.nav-item--active {
  background: var(--sd-sidebar-active-bg);
  color: var(--sd-accent);
}

.nav-item--active :deep(.nav-icon) {
  color: var(--sd-accent);
}

/* 订单口径小卡:真数落点(画板没画,见文件头) */
.order-scope {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface-alt);
}

.os-title {
  margin: 0;
  font-size: 12px;
  color: var(--sd-text2);
}

.os-range {
  margin: 0;
  font-size: 10px;
  color: var(--sd-text3);
}

.os-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.os-label {
  font-size: 11px;
  color: var(--sd-text3);
}

.os-value {
  font-size: 12px;
  color: var(--sd-text1);
}

.os-error {
  margin: 0;
  font-size: 10px;
  line-height: 1.6;
  color: var(--sd-danger);
  word-break: break-all;
}

.os-loading {
  margin: 0;
  font-size: 10px;
  color: var(--sd-text3);
}

/* 侧栏底:实拍图 + 口径小字(画板 Sidebar 的底图与底部口径) */
.sidebar-foot {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.hero {
  margin: 0;
  width: 188px;
  height: 130px;
  overflow: hidden;
  border-radius: var(--sd-radius-sm);
}

.hero-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sidebar-note {
  margin: 0;
  font-size: 11px;
  line-height: 1.7;
  color: var(--sd-text3);
}

/* ── 中央列(1174)─────────────────────────────────── */

.center {
  flex: none;
  width: 1174px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
}

/* KPI 行(高 162:四张 217 数值卡 + 一张 250 运行状态卡) */
.kpi-row {
  flex: none;
  height: 162px;
  display: flex;
  justify-content: space-between;
}

.kpi-card {
  width: 217px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
}

.kpi-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kpi-icon {
  color: var(--sd-accent);
}

.kpi-label {
  font-size: 15px;
  color: var(--sd-text2);
  white-space: nowrap;
}

.kpi-value {
  font-size: 44px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--sd-text1);
}

.kpi-caption {
  margin: auto 0 0;
  font-size: 12px;
  color: var(--sd-text3);
}

.run-card {
  width: 250px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
}

.run-title {
  margin: 0;
  font-size: 15px;
  font-weight: 400;
  color: var(--sd-text2);
}

.run-rows {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.run-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.run-dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: var(--sd-radius-pill);
}

.run-dot--success { background: var(--sd-success); }
.run-dot--info { background: var(--sd-info); }
.run-dot--danger { background: var(--sd-danger); }
.run-dot--muted { background: var(--sd-text3); }

.run-name {
  font-size: 13px;
  color: var(--sd-text2);
}

.run-count {
  margin-left: auto;
  font-size: 13px;
  color: var(--sd-text1);
}

/* 实时轨迹面板(高 624:标题行 + 船舶列表 250 + 地图) */
.track-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
}

.track-head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.track-title {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.track-title h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--sd-text1);
}

.track-sub {
  font-size: 12px;
  color: var(--sd-text2);
}

.ship-search {
  flex: none;
  width: 300px;
  height: 33px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-surface-alt);
}

.ship-search-icon {
  color: var(--sd-text3);
}

.ship-search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 12px;
  color: var(--sd-text1);
}

.ship-search-input::placeholder {
  color: var(--sd-text3);
}

.track-body {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 12px;
}

.ship-list {
  flex: none;
  width: 250px;
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ship-row {
  height: 53px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-surface-alt);
}

.ship-icon {
  color: var(--sd-info);
}

.ship-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ship-name {
  font-size: 13px;
  color: var(--sd-text1);
  white-space: nowrap;
}

.ship-meta {
  font-size: 11px;
  color: var(--sd-text3);
  white-space: nowrap;
}

.ship-status {
  margin-left: auto;
  font-size: 11px;
  white-space: nowrap;
}

.ship-status--success { color: var(--sd-success); }
.ship-status--info { color: var(--sd-info); }
.ship-status--danger { color: var(--sd-danger); }
.ship-status--muted { color: var(--sd-text3); }

.ship-empty {
  padding: 12px 8px;
  font-size: 12px;
  color: var(--sd-text3);
}

/* 地图区(880 宽):底图铺满 + 标注层 */
.map {
  position: relative;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-bg-deep);
}

.map-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.map-layer {
  position: absolute;
  inset: 0;
}

.pad {
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transform: translateY(-50%);
  font-size: 12px;
  color: var(--sd-text1);
  white-space: nowrap;
}

.pad-dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: var(--sd-radius-pill);
  background: var(--sd-accent);
}

.map-ship {
  position: absolute;
  transform: translate(-50%, -50%);
  color: var(--sd-info);
}

.map-anchor {
  position: absolute;
  transform: translate(-50%, -50%);
  color: var(--sd-text3);
}

.warning-circle {
  position: absolute;
  width: 120px;
  height: 120px;
  border: 2px solid var(--sd-danger);
  border-radius: 50%;
}

.warning-tag {
  position: absolute;
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%);
  padding: 3px 8px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-danger-bg);
  color: var(--sd-danger);
  font-size: 11px;
  white-space: nowrap;
}

.map-legend {
  position: absolute;
  top: 12px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: var(--sd-text2);
  white-space: nowrap;
}

.legend-dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: var(--sd-radius-pill);
}

.legend-dot--success { background: var(--sd-success); }
.legend-dot--info { background: var(--sd-info); }
.legend-dot--danger { background: var(--sd-danger); }
.legend-dot--muted { background: var(--sd-text3); }

.map-scale {
  position: absolute;
  right: 16px;
  bottom: 12px;
  font-size: 11px;
  color: var(--sd-text3);
}

/* 底行(高 150:实时事件卡 400 + 六枚统计块 115) */
.bottom-row {
  flex: none;
  height: 150px;
  display: flex;
  gap: 14px;
}

.events-card {
  flex: none;
  width: 400px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
}

.events-head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.events-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--sd-text1);
}

.events-more {
  font-size: 11px;
  color: var(--sd-accent);
}

.events-list {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.event-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.event-dot {
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: var(--sd-radius-pill);
}

.event-dot--danger { background: var(--sd-danger); }
.event-dot--info { background: var(--sd-info); }
.event-dot--warning { background: var(--sd-warning); }
.event-dot--muted { background: var(--sd-text3); }

.event-time {
  flex: none;
  font-size: 11px;
  color: var(--sd-text3);
}

.event-text {
  font-size: 12px;
  color: var(--sd-text1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-dept {
  margin-left: auto;
  flex: none;
  font-size: 11px;
  white-space: nowrap;
}

.event-dept--danger { color: var(--sd-danger); }
.event-dept--info { color: var(--sd-info); }
.event-dept--warning { color: var(--sd-warning); }
.event-dept--muted { color: var(--sd-text3); }

/* 六枚统计块:顶对齐、高随内容(子行一行 86 / 两行 100,画板如此) */
.stat-block {
  flex: none;
  width: 115px;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
}

.stat-name {
  font-size: 12px;
  color: var(--sd-text2);
}

.stat-count {
  font-size: 16px;
  font-weight: 700;
}

.stat-count--info { color: var(--sd-info); }
.stat-count--success { color: var(--sd-success); }
.stat-count--warning { color: var(--sd-warning); }
.stat-count--danger { color: var(--sd-danger); }

.stat-sub {
  font-size: 10px;
  line-height: 1.5;
  color: var(--sd-text3);
}

/* ── 右栏(470,两块各半)──────────────────────────── */

.rail {
  flex: none;
  width: 470px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.warehouse-card,
.retention-card {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
}

.card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--sd-text1);
}

.warehouse-note {
  margin: 0;
  font-size: 11px;
  line-height: 1.7;
  color: var(--sd-text3);
}

.dept-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dept-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface-alt);
  font-size: 12px;
  color: var(--sd-text2);
}

.dept-dot {
  flex: none;
  width: 5px;
  height: 5px;
  border-radius: var(--sd-radius-pill);
}

.dept-dot--ok { background: var(--sd-success); }
.dept-dot--warn { background: var(--sd-warning); }

/* 直播流矩阵:两行 × 三格(与 G1 同款:实拍图铺满 + 顶行徽标/通道号 + 底部点位名) */
.video-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 8px;
}

.video-cell {
  position: relative;
  margin: 0;
  overflow: hidden;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-bg-deep);
}

.video-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, var(--sd-bg-deep) 0%, transparent 32%, transparent 55%, var(--sd-bg-deep) 100%);
  opacity: 0.72;
}

.video-top {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.video-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 1px 6px;
  border-radius: var(--sd-radius-sm);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.video-badge--live {
  background: var(--sd-danger-bg);
  border: 1px solid var(--sd-danger-border);
  color: var(--sd-danger);
}

.video-badge--rec {
  background: var(--sd-warning-bg);
  border: 1px solid var(--sd-warning-border);
  color: var(--sd-warning);
}

.video-badge-dot {
  width: 5px;
  height: 5px;
  flex: none;
  border-radius: var(--sd-radius-pill);
  background: currentColor;
}

.video-ch {
  font-size: 10px;
  color: var(--sd-text3);
}

.video-point {
  position: absolute;
  left: 8px;
  bottom: 6px;
  font-size: 10px;
  color: var(--sd-text2);
}

/* 视频留存 & 回看(整块形态占位) */
.vrc-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
}

.vrc-head :deep(.trigger) {
  margin-left: auto;
}

.search-rows {
  flex: none;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.search-row {
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 12px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-surface-alt);
}

.search-row dt {
  font-size: 12px;
  color: var(--sd-text3);
  white-space: nowrap;
}

.search-row dd {
  margin: 0;
  font-size: 13px;
  color: var(--sd-text1);
  white-space: nowrap;
}

/* 回放时间轴(全板唯一带图型的元素;位置与宽度是画板的示例值) */
.timeline {
  flex: none;
  margin-top: 4px;
}

.tl-track {
  position: relative;
  height: 14px;
}

.tl-rail {
  position: absolute;
  left: 0;
  right: 0;
  top: 4px;
  height: 6px;
  border-radius: var(--sd-radius-pill);
  background: var(--sd-surface-alt);
}

.tl-played {
  position: absolute;
  left: 0;
  top: 4px;
  width: 250px;
  height: 6px;
  border-radius: var(--sd-radius-pill);
  background: var(--sd-accent);
}

.tl-alarm {
  position: absolute;
  left: 240px;
  top: 0;
  width: 80px;
  height: 14px;
  border-radius: 3px;
  background: var(--sd-warning);
}

.tl-ticks {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 11px;
  color: var(--sd-text3);
}

.act-row {
  margin-top: auto;
  display: flex;
  gap: 8px;
}

.act {
  flex: 1;
  height: 44px;
  border-radius: var(--sd-radius-sm);
  font-family: inherit;
  font-size: 14px;
  border: 1px solid var(--sd-border-strong);
  background: transparent;
  color: var(--sd-text2);
}

.act--primary {
  border: none;
  background: var(--sd-accent);
  color: var(--sd-bg-deep);
  font-weight: 700;
}

/* 两个按钮是形态占位:禁用态(不接任何数据),不许被读成能点 —— 见模板上的 @unimplemented */
.act:disabled {
  cursor: not-allowed;
  opacity: 0.9;
}

/* ── 装饰 · 面板四角小角框(accent L 形,与 G1 同款)── */
.brackets {
  position: absolute;
  inset: 6px;
  pointer-events: none;
  background-image:
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent));
  background-repeat: no-repeat;
  background-size: 12px 1px, 1px 12px, 12px 1px, 1px 12px, 12px 1px, 1px 12px, 12px 1px, 1px 12px;
  background-position:
    left top, left top,
    right top, right top,
    left bottom, left bottom,
    right bottom, right bottom;
}
</style>
